/*
  ── A DELIBERATE FORK of <IndexPage> ─────────────────────────────────────────────────

  The plantilla's own index shell, copied from the shared one so that changes made for the
  staffing pattern cannot reach the modules that share `<IndexPage>`. It renders
  `<PlantillaIndexTable>` rather than the standard table and opens in TABLE view, both of
  which are plantilla decisions.

  Everything else it composes — the toolbar, the filter rail, the pager, the list view, the
  selection bar, the History Logs modal, `useIndexQuery` — is still the shared component, so
  a search strip, a column drag and a pager behave here exactly as they do everywhere else.
  Only the table and the default view are local.

  The cost is the same as the table's: improvements to the shared shell do not arrive here.
  Diff the two before changing either.
*/
import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { FolderTabs, type FolderTab } from '../../../../components/FolderTabs';
import { useFolderNavFacet } from '../../../../components/index-page/useFolderNavFacet';
import { DateCell } from '../../../../components/table/DateCell';
import { TextCell } from '../../../../components/table/TextCell';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexListView } from '../../../../components/index-page/IndexListView';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { IndexFilterRail } from '../../../../components/index-page/IndexFilterRail';
import { IndexPagination } from '../../../../components/index-page/IndexPagination';
import { IndexSelectionBar } from '../../../../components/index-page/IndexSelectionBar';
import { PlantillaIndexTable } from './PlantillaIndexTable';
import { IndexToolbar } from '../../../../components/index-page/IndexToolbar';
import { composePrerequisites } from '../../../../components/index-page/prerequisites';
import { PrerequisitesBanner } from '../../../../components/index-page/PrerequisitesBanner';
import type { IndexPageProps } from '../../../../components/index-page/types';
import type { PlantillaColumnDef } from './PlantillaIndexTable';

import { useIndexQuery } from '../../../../components/index-page/useIndexQuery';
import { useFilterRail } from '../../../../components/index-page/useIndexViewType';
import { usePlantillaViewType } from './usePlantillaViewType';

/**
 * The shared shell's props, with one change: `onCreate` is told what the list is scoped to.
 *
 * A create form opened from a filtered list can seed itself from the filter — the reader
 * narrowed the pattern to a unit and is now adding a post to it. The shared shell keeps
 * `onCreate` as a bare callback, so this is exactly the kind of local change the fork exists
 * for.
 */
type PlantillaShellProps<T> = Omit<IndexPageProps<T>, 'onCreate' | 'columns'> & {
  /** The plantilla table's columns — the shared shape, plus groups, merges and upright text. */
  columns: PlantillaColumnDef<T>[];
  onCreate?: (context: {
    /** The org units the list is filtered to. Empty when the reader has not narrowed it. */
    unitIds: string[];
  }) => void;
};

/** The `All` tab: no constraint on the nav axis at all, which is not itself a value on it. */
const ALL_VALUES = '__all__';

/**
 * The `None` tab: the axis is constrained to no value, so no row qualifies.
 *
 * It is offered ONLY while that state holds, because it is a state to get out of rather than
 * one to navigate to — the rail is where it is entered, by unticking the last value. The
 * strip still has to be able to show it: `<FolderTabs>` corrects an active key it cannot
 * find to the first visible tab, so leaving this unrepresented would silently snap the axis
 * back to All and undo the reader's selection.
 */
const NO_VALUE = '__none__';

/**
 * The canonical list shell — reference §3.
 *
 * Every module's primary list routes through this. Feature pages configure columns,
 * facets, and the endpoint; they never roll their own layout. Rolling a bespoke list page
 * is drift to migrate on sight.
 */
export function PlantillaIndexShell<T>({
  module,
  breadcrumb,
  columns,
  facets,
  auditEntityTypes,
  auditColumns = true,
  listAction = 'created',
  freezeAnchor = false,
  rowKey,
  fetch,
  reloadKey = 0,
  searchPlaceholder = 'Search',
  emptyMessage,
  onCreate,
  createPermission,
  createDisabledReason,
  prerequisites = [],
  rowActions,
  onRowOpen,
  openPermission,
  onEdit,
  editPermission,
  onDelete,
  deletePermission,
  deleteLabel,
  deleteDisabledReason,
  otherInfo,
  selectable,
  bulkActions,
  onClearData,
  clearDataPermission,
  folderNavFacet,
}: PlantillaShellProps<T>) {
  const { hasPermission } = useAuth();
  const query = useIndexQuery<T>(module, fetch, reloadKey);

  /*
    What the staffing pattern cannot be USED without — composed by the SHARED module, not here.

    This shell is a fork of `<IndexPage>`, and its standing cost is that improvements to the
    shared one do not arrive here. A banner whose wording lived in both files would be a
    sentence corrected in one of them, so only the RENDER is local and the derivation is not.
  */
  const composedPrerequisites = composePrerequisites(prerequisites);

  // Table first here — see usePlantillaViewType.
  const { viewType, setViewType } = usePlantillaViewType(module);
  const { open: railOpen, setOpen: setRailOpen } = useFilterRail(module);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [query.items]);

  /*
    Status is always the last data column. A stable partition, not a sort: everything else
    keeps the order the feature page declared, and the status column lands at the end even
    if the page put it in the middle. Enforcing it here is what makes "always" true — the
    alternative is every future page remembering, which is how drift starts.
  */
  /*
    Created / Created by are appended by the shell, not declared by each page. They read
    from `otherInfo` — the audit contract every index page already supplies — so no feature
    page has to remember them and none can render them differently.

    Not sortable: the sort field allow-list is per-module on the server, and audit-log-style
    tables order on their own timestamp field. A sort control that silently fell back to the
    default would be worse than none.
  */
  const auditPair: PlantillaColumnDef<T>[] = auditColumns
    ? [
        {
          key: 'createdAt',
          header: 'Created',
          width: 175,
          render: (row: T) => <DateCell value={otherInfo(row)?.createdAt ?? null} fallback="—" />,
        },
        {
          key: 'createdByName',
          header: 'Created by',
          width: 190,
          // Same 13px as the date beside it: the pair is one fact split across two columns.
          render: (row: T) => <TextCell value={otherInfo(row)?.createdByName ?? null} mono size="xs" />,
        },
        {
          /*
            Latest update, immediately before status. Blank on a record never touched since
            creation — an em-dash there would claim an update that did not happen. The
            actor half stays in the Other Info popover; only the instant earns a column.
          */
          key: 'updatedAt',
          header: 'Latest update',
          width: 175,
          render: (row: T) => <DateCell value={otherInfo(row)?.updatedAt ?? null} fallback="" />,
        },
      ]
    : [];

  /*
    A column or a facet the caller lacks the key for is removed here, once, before anything
    downstream sees it — so the table, the list view, the totals footer, the column-width
    settings, and the filter rail cannot disagree about what is visible. Resolving it in
    each of those instead is how two surfaces end up showing different things.
  */
  const visibleColumns = columns.filter(
    (column) => !column.permission || hasPermission(column.permission),
  );

  const { facet: navFacet, setFacet: setNavFacet } = useFolderNavFacet(
    module,
    // The page declares the default axis (`folderNavFacet`, as on <IndexPage>); the reader
    // repoints it from the rail's cog. `null` is no strip at all, so the hook gets status.
    folderNavFacet ?? 'status',
    facets
      .filter((facet) => !facet.permission || hasPermission(facet.permission))
      .map((facet) => facet.key),
  );

  const visibleFacets = facets.filter(
    (facet) => !facet.permission || hasPermission(facet.permission),
  );

  // Status stays last; the audit pair sits immediately before it.
  const orderedColumns = [
    ...visibleColumns.filter((column) => !column.status),
    ...auditPair,
    ...visibleColumns.filter((column) => column.status),
  ];

  /*
    The list view's columns: the table's, minus any that merge rows together. See the
    <IndexListView> call for why — a merge is a statement about a run of rows, and a list row
    is one record.
  */
  const listColumns = orderedColumns.filter(
    (column) => !column.mergeKey && !column.groupCounter,
  );

  const selectedRows = query.items.filter((row) => selectedKeys.has(rowKey(row)));

  /*
    The anchor column's click target. Edit is the default, because for a CRUD module
    "open the record" is the edit form; a read-only module supplies `onRowOpen` instead of
    growing a fake Edit. Either way it is wired only when the viewer can actually use it,
    so the darker anchor is never a dead affordance.
  */
  const openRow = onRowOpen ?? onEdit;
  const openRowPermission = onRowOpen ? openPermission : editPermission;
  const canOpenRow = openRow && (!openRowPermission || hasPermission(openRowPermission));

  const isFiltered = query.search.length > 0 || query.activeFilterCount > 0;

  /*
    One wrapper, handed to every Create affordance — the toolbar, the selection bar and the
    empty state — so all three open the form with the same scope. Reading the facet here is
    what lets the form seed itself from a filtered list.
  */
  const createHandler = onCreate
    ? () => onCreate({ unitIds: query.facets.unit ?? [] })
    : undefined;

  /*
    Multi-select is STANDARD, not opt-in. Every list of records that can be acted on gets the
    checkbox column, in both views, without the feature page asking — a module that quietly
    shipped without one was a list where the same job took twenty clicks instead of two.

    Derived rather than defaulted to `true`, so it stays honest at both ends: a module with
    something to do to a selection (`onDelete`, or its own `bulkActions`) gets it, and one
    with neither does not. That is what keeps the checkbox off the audit trail, which is
    append-only and where a selection could never lead anywhere. A page can still force the
    answer either way by passing `selectable` explicitly.
  */
  const rowsAreSelectable = selectable ?? (onDelete !== undefined || bulkActions !== undefined);

  /*
    Clear data. A pure permission gate, so the entry is HIDDEN rather than disabled when the
    caller lacks the key — a control they can never enable is noise.

    The shell owns the entry, the confirmation and the reload; the page owns only the call,
    because the shell knows no endpoints. That split is what stops one module confirming a
    mass deletion differently from the next.
  */
  const canClearData =
    onClearData !== undefined && (!clearDataPermission || hasPermission(clearDataPermission));

  const moduleNoun = module.replace('-', ' ');

  /*
    The folder nav — a second control over an axis the rail already exposes, on the product
    owner's call: a cut people take constantly is worth one click rather than two through the
    collapsed rail.

    It opens on the page's `folderNavFacet` — the plantilla passes VACANCY (product call, 19
    Oct 2026: `All · Has a vacancy · Fully filled`), the axis a staffing pattern is read by —
    and the reader repoints it from the rail's cog, remembered per module.

    Two controls, ONE piece of state. The active tab is DERIVED from `query.facets[navFacet]`
    rather than held beside it — the same reasoning as `showDeleted`. A tab index with its
    own state would let the strip and the rail disagree, and the list can only obey one of
    them.

    `All` clears that axis entirely. On status that is also what makes deleted rows eligible,
    so it moves the settings cog's Show deleted checkbox — the same axis read a third way.
    Pointed anywhere else it moves nothing but itself, because nothing else reads that axis.
  */
  const navFacetLabel = facets.find((facet) => facet.key === navFacet)?.label ?? navFacet;

  const navValues = folderNavFacet === null ? [] : query.facetCounts[navFacet] ?? [];
  const chosenNavValues = query.facets[navFacet] ?? [];

  // Present but empty: the reader unticked every value. Distinct from never having filtered.
  const noNavValueChosen = navFacet in query.facets && chosenNavValues.length === 0;

  /*
    `All · Active · Inactive` DRAWS NOTHING — <IndexPage>'s rule (12 Oct 2026), which this fork
    had missed: the rail already carries that axis and the resting query is already active.
    A reader who repoints the strip back to status gets nothing drawn, the same as everywhere.
  */
  const navIsDefaultStatus =
    navFacet === 'status' &&
    navValues.length === 2 &&
    navValues.every((value) => value.value === 'active' || value.value === 'inactive');

  const navTabs: FolderTab<string>[] =
    navValues.length > 0 && !navIsDefaultStatus
      ? [
          {
            key: ALL_VALUES,
            /*
              **All carries NO count**, which is the rail's own rule reaching the strip.

              It used to sum the values below it, and that is only the truth where they
              PARTITION the list. They do on status; they do not on an axis that caps itself
              (the audit trail offers its top 20 actions) or drops rows it cannot label (a log
              written outside a request has no method) — and pointing the nav at one of those
              is something a reader can now do from the rail's cog. It read `All 2134` over a
              list of 2,259, which is the strip lying about what is on screen.

              Nothing is lost by dropping it: with the axis unconstrained the number equalled
              the toolbar's record count three inches away, so it was the same value in two
              slots on exactly the surfaces where it was correct.
            */
            label: <>All</>,
          },
          ...navValues.map((value) => ({
            key: value.value,
            label: (
              <>
                {value.label}
                <CountBadge count={value.count} tone="slate" />
              </>
            ),
          })),

          // Only while it is the state, and it always reads zero — that is the whole point.
          ...(noNavValueChosen
            ? [
                {
                  key: NO_VALUE,
                  label: (
                    <>
                      None
                      <CountBadge count={0} tone="slate" />
                    </>
                  ),
                },
              ]
            : []),
        ]
      : [];

  /*
    One value ticked is that tab. The axis unmentioned is All, and every value ticked is also
    All — with the whole axis selected the result set is identical, so claiming otherwise
    would be the strip lying about what the reader is looking at.

    Ticked-to-nothing is its own tab. It is NOT All: All shows every row and this shows none,
    and the rail already draws the difference.
  */
  const activeNavValue = noNavValueChosen
    ? NO_VALUE
    : chosenNavValues.length === 1 && chosenNavValues[0] !== undefined
      ? chosenNavValues[0]
      : ALL_VALUES;

  const rowChrome = {
    module,
    columns: orderedColumns,
    rows: query.items,
    rowKey,
    firstRow: query.firstRow,
    loading: query.loading,
    otherInfo,
    rowActions,
    onRowOpen: canOpenRow ? openRow : undefined,
    onEdit,
    editPermission,
    onDelete,
    deletePermission,
    deleteLabel,
    deleteDisabledReason,
  };

  /*
    Selection belongs to the list, not to one view of it. Both surfaces get the same props
    and read the same `selectedKeys`, so a selection survives a view switch and the
    selection bar behaves identically on either side. Handing it only to the table was how
    the checkbox column went missing from the list view.
  */
  const selection = {
    selectable: rowsAreSelectable,
    selectedKeys,
    onToggleRow: (key: string) =>
      setSelectedKeys((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    onToggleAll: () =>
      setSelectedKeys((current) =>
        current.size === query.items.length
          ? new Set()
          : new Set(query.items.map(rowKey)),
      ),
  };

  return (
    // Full-bleed shell: no page title, no outer card, per-panel scrolling.
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      {/* Conditional for the reason `<IndexPage>`'s is: the prop became optional when Stock
          Levels dropped its crumb. This shell shares that props type, so it inherits the
          question — and the plantilla is reached from the sidebar, so it could drop its own. */}
      {breadcrumb ? <Breadcrumb segments={breadcrumb} /> : null}

      <div className="flex min-h-0 min-w-0 flex-1">
        <IndexFilterRail
          module={module}
          open={railOpen}
          // The same close the toolbar's filter button makes — see the index shell, which
          // this one is a fork of and owes every prop the rail grows.
          onClose={() => setRailOpen(false)}
          facets={visibleFacets}
          counts={query.facetCounts}
          selected={query.facets}
          /*
            One handler for the whole rail: ticking a value, unticking one, and All are all
            "this axis now holds exactly these values". All is the empty list — the same
            call the status strip's All tab makes, so the two cannot mean different things.
          */
          onSelect={query.selectFacet}
          onClear={query.clearFacet}
          navFacet={navFacet}
          onNavFacetChange={setNavFacet}
          loading={query.loading}
        />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {rowsAreSelectable && selectedKeys.size > 0 ? (
            <IndexSelectionBar
              module={module}
              selectedCount={selectedKeys.size}
              bulkItems={bulkActions?.(selectedRows) ?? []}
              onCreate={createHandler}
              createPermission={createPermission}
              onClear={() => setSelectedKeys(new Set())}
            />
          ) : (
          <IndexToolbar
            module={module}
            searchValue={query.searchInput}
            onSearchChange={query.setSearchInput}
            searchPlaceholder={searchPlaceholder}
            total={query.total}
            loading={query.loading}
            activeFilterCount={query.activeFilterCount}
            filterRailOpen={railOpen}
            onToggleFilterRail={() => setRailOpen(!railOpen)}
            onCreate={createHandler}
            createPermission={createPermission}
            /*
              The prerequisite sentence names ALL of the missing catalogs, while the banner
              above names one — see `composePrerequisites`. A page's own reason still wins: that
              is a state about the records in front of the reader, not about whether the module
              has ever been set up.
            */
            createDisabledReason={createDisabledReason ?? composedPrerequisites.reason}
            viewType={viewType}
            onViewTypeChange={setViewType}
            showDeleted={query.showDeleted}
            onShowDeletedChange={query.setShowDeleted}
            /*
              A module declares the axis by declaring the facet. Audit logs have no
              `status` facet, because an append-only trail has nothing to delete.
            */
            supportsDeleted={facets.some((facet) => facet.key === 'status')}
            onOpenHistory={() => setHistoryOpen(true)}
            onClearData={canClearData ? () => setClearOpen(true) : undefined}
          />
          )}

          {/*
            The table sits in a padded, rounded panel rather than bleeding to the shell
            edges. The padding is on the outer box and the scroll on the inner one, so the
            rounded corners clip the sticky header and footer instead of scrolling with
            them.
          */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4">
            {/* What the module cannot be USED without, stated before the reader tries. */}
            <PrerequisitesBanner module={module} composed={composedPrerequisites} />

            {/*
              The status folder nav carries NO ground of its own — the tabs stand directly on
              the shell's slate-50, which is the one surface behind them.

              This departs from the strip's usual three-surface stack (slate-100 behind
              slate-50 tabs behind a white active one), and it holds here because the index
              shell's own ground is already slate-50: an inactive tab reads as flush with the
              page and is defined by its outline, while the active tab is the only white thing
              on the row and lifts forward into the panel it merges with. The stack does not
              invert — white is still frontmost — it just has two of its three tones equal.

              Indented past the panel's corner radius, so the first tab does not sit over the
              curve it is supposed to merge into.
            */}
            {navTabs.length > 0 ? (
              <FolderTabs
                id={`${module}-${navFacet}`}
                ariaLabel={`Filter by ${navFacetLabel.toLowerCase()}`}
                tabs={navTabs}
                activeKey={activeNavValue}
                paddingX="px-3"
                onChange={(key) =>
                  key === ALL_VALUES
                    ? query.clearFacet(navFacet)
                    : query.selectFacet(navFacet, key === NO_VALUE ? [] : [key])
                }
              />
            ) : null}

            {/*
              One box, both views. The list view is a divided list inside this frame rather
              than a stack of floating cards, so the panel edge is the only edge either view
              draws.
            */}
            {/*
              `isolate` contains the table's own stacking — the sticky header is `z-20` and
              its pinned cells `z-30`, which without a stacking context here compete with the
              tab strip outside and paint over the active tab's 1px merge. Isolated, those
              indices only rank against each other and the panel as a whole sits below the
              tabs, which is the order the folder metaphor needs.
            */}
            <div className="modern-scrollbar isolate min-h-0 min-w-0 flex-1 overflow-auto rounded-panel border border-slate-300 bg-white">
              {query.error ? (
                <ErrorState
                  message={query.error.message}
                  correlationId={query.error.correlationId}
                  onRetry={() => void query.reload()}
                />
              ) : !query.loading && query.items.length === 0 ? (
                <EmptyState
                  message={
                    isFiltered
                      ? `No ${module.replace('-', ' ')} match the current search or filters.`
                      : (emptyMessage ?? `No ${module.replace('-', ' ')} yet.`)
                  }
                  action={
                    createHandler && !isFiltered
                      ? { label: 'Create', onClick: createHandler, permission: createPermission }
                      : undefined
                  }
                />
              ) : viewType === 'list' ? (
                <IndexListView
                  {...rowChrome}
                  /*
                    Merged columns are dropped from the list, and that is not a cosmetic
                    choice: a merge groups adjacent ROWS, which a two-line list row has no
                    way to express, and leaving it in would make the merged column the list's
                    anchor — so the reader would open a record by clicking a value shared by
                    several of them.
                  */
                  columns={listColumns}
                  /* One flag, both views: a table whose own columns are the creation
                     metadata is also a list that must not lead with it. */
                  provenanceLead={auditColumns}
                  {...selection}
                  listAction={listAction}
                  onRowOpen={canOpenRow ? openRow : undefined}
                />
              ) : (
                <PlantillaIndexTable
                  {...rowChrome}
                  {...selection}
                  sort={query.sort}
                  onToggleSort={query.toggleSort}
                  freezeAnchor={freezeAnchor}
                />
              )}
            </div>
          </div>

          <IndexPagination
            module={module}
            page={query.page}
            pageCount={query.pageCount}
            firstRow={query.firstRow}
            lastRow={query.lastRow}
            total={query.total}
            loading={query.loading}
            onPageChange={query.setPage}
          />
        </div>
      </div>

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module={module}
        entityTypes={auditEntityTypes}
      />

      {/*
        The consequence is stated on the form, before the tick — CLAUDE.md, "a consequence
        goes on the form, not on the tick". It says what will happen, that it is reversible,
        and exactly how to reverse it, because a reader deciding whether to do this needs the
        way back in front of them rather than in a runbook.
      */}
      <ConfirmDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        id={`${module}-clear-data-dialog`}
        title={`Clear all ${moduleNoun}`}
        danger
        requireConfirm
        // The widest destructive action in the product: both safeguards, always.
        requireCredential
        loading={clearing}
        body={
          <div className="space-y-2">
            <p>
              Every active record in {moduleNoun} will be deleted at once.
            </p>
            <p>
              Nothing is erased. Deleted rows stay in the database and come back into view
              through <span className="font-medium text-slate-700">Show deleted</span> in this
              menu, from where each one can be restored.
            </p>
          </div>
        }
        onConfirm={async () => {
          if (!onClearData) return;

          setClearing(true);
          try {
            await onClearData();
            setClearOpen(false);
            // The list on screen is now entirely wrong, so it is refetched rather than
            // patched — the status counts above it moved too.
            await query.reload();
          } finally {
            setClearing(false);
          }
        }}
      />
    </div>
  );
}
